/**
 * Batch Location Creation - Configuration Models
 * 
 * Modelos para configurar la creación en lote de ubicaciones
 * con estructura jerárquica y nomenclatura personalizable.
 * 
 * @module BatchCreationConfig
 */

/**
 * Profundidad de la estructura jerárquica
 * - 1: SALON → POSITION
 * - 2: SALON → ROW → POSITION
 * - 3: SALON → ROW → RACK → POSITION
 * - 4: SALON → ROW → RACK → LEVEL → POSITION
 */
export type LocationDepth = 1 | 2 | 3 | 4;

/**
 * Formato de consecutivos numéricos
 * - 'numeric': 1, 2, 3, ..., n (default)
 * - 'padded': 01, 02, 03, ..., n (con ceros a la izquierda)
 */
export type ConsecutiveFormat = 'numeric' | 'padded';

/**
 * Dimensiones de cada nivel de la estructura
 * Cada propiedad representa la cantidad de elementos a crear en ese nivel
 */
export interface BatchDimensions {
  /**
   * Cantidad de filas (ROW) - Profundidad >= 2
   * @minimum 1
   * @maximum 999
   */
  rows?: number;

  /**
   * Cantidad de racks (RACK-FRONT) por fila - Profundidad >= 3
   * @minimum 1
   * @maximum 999
   */
  racks?: number;

  /**
   * Cantidad de niveles (LEVEL-FRONT) por rack - Profundidad = 4
   * @minimum 1
   * @maximum 999
   */
  levels?: number;

  /**
   * Cantidad de posiciones (POSITION-FRONT) en el último nivel
   * Este campo es obligatorio para todas las profundidades
   * @minimum 1
   * @maximum 999
   */
  positions: number;
}

/**
 * Prefijos personalizables para nomenclatura de cada nivel
 * Si no se especifica, se usan los valores por defecto
 */
export interface NomenclaturePrefixes {
  /**
   * Prefijo para ROW (Fila)
   * @default "F"
   * @minLength 0
   * @maxLength 10
   * @pattern ^[a-zA-Z0-9-]*$
   */
  row?: string;

  /**
   * Prefijo para RACK-FRONT (Rack)
   * @default "R"
   * @minLength 0
   * @maxLength 10
   * @pattern ^[a-zA-Z0-9-]*$
   */
  rack?: string;

  /**
   * Prefijo para LEVEL-FRONT (Nivel)
   * @default "L"
   * @minLength 0
   * @maxLength 10
   * @pattern ^[a-zA-Z0-9-]*$
   */
  level?: string;

  /**
   * Prefijo para POSITION-FRONT (Posición)
   * @default "P"
   * @minLength 0
   * @maxLength 10
   * @pattern ^[a-zA-Z0-9-]*$
   */
  position?: string;
}

/**
 * Configuración completa para creación en lote de ubicaciones
 */
export interface BatchCreationConfig {
  /**
   * ID del salón padre donde se crearán las ubicaciones
   * Debe ser un SALON existente en el sistema
   */
  parentSalonId: number;

  /**
   * Nombre del salón padre (para preview y display)
   */
  parentSalonName: string;

  /**
   * Profundidad de la estructura jerárquica (1-4 niveles)
   */
  depth: LocationDepth;

  /**
   * Dimensiones: cantidad de elementos por nivel
   */
  dimensions: BatchDimensions;

  /**
   * Prefijos personalizados para nomenclatura (opcional)
   * Si no se especifica, se usan los valores por defecto
   */
  prefixes?: NomenclaturePrefixes;

  /**
   * Formato de consecutivos (opcional)
   * @default 'numeric'
   */
  consecutiveFormat?: ConsecutiveFormat;

  /**
   * Cantidad de dígitos para formato padded (default: 2)
   * Solo aplica si consecutiveFormat = 'padded'
   * Ejemplo: paddingDigits = 3 → 001, 002, 003
   * @minimum 2
   * @maximum 5
   * @default 2
   */
  paddingDigits?: number;

  /**
   * ID del customer propietario de las ubicaciones
   */
  customerId: number;

  /**
   * Observaciones o notas adicionales (opcional)
   */
  notes?: string;
}

/**
 * Prefijos por defecto para cada tipo de ubicación
 */
export const DEFAULT_PREFIXES: Required<NomenclaturePrefixes> = {
  row: 'F',
  rack: 'R',
  level: 'L',
  position: 'P',
};

/**
 * Validador de prefijo personalizado
 * Retorna true si el prefijo es válido
 * 
 * @param prefix - Prefijo a validar
 * @returns true si es válido, false en caso contrario
 */
export function isValidPrefix(prefix: string | undefined): boolean {
  if (!prefix) return true; // Vacío es válido (usa default)
  
  // Validación: 0-10 caracteres, solo letras, números y guiones
  const regex = /^[a-zA-Z0-9-]{0,10}$/;
  return regex.test(prefix);
}

/**
 * Obtiene el prefijo efectivo (personalizado o default)
 * 
 * @param customPrefix - Prefijo personalizado (puede ser undefined)
 * @param defaultPrefix - Prefijo por defecto
 * @returns Prefijo a usar
 */
export function getEffectivePrefix(
  customPrefix: string | undefined,
  defaultPrefix: string
): string {
  return customPrefix !== undefined && customPrefix !== '' 
    ? customPrefix 
    : defaultPrefix;
}

/**
 * Calcula el total de ubicaciones a crear según la configuración
 * 
 * Fórmulas por profundidad:
 * - Depth 1: P
 * - Depth 2: F + (F × P)
 * - Depth 3: F + (F × R) + (F × R × P)
 * - Depth 4: F + (F × R) + (F × R × L) + (F × R × L × P)
 * 
 * @param config - Configuración de creación en lote
 * @returns Total de ubicaciones a crear
 */
export function calculateTotalLocations(config: BatchCreationConfig): number {
  const { depth, dimensions } = config;
  const { rows = 0, racks = 0, levels = 0, positions } = dimensions;

  switch (depth) {
    case 1:
      // Solo posiciones directas bajo el salón
      return positions;

    case 2:
      // Filas + (Filas × Posiciones)
      return rows + (rows * positions);

    case 3:
      // Filas + (Filas × Racks) + (Filas × Racks × Posiciones)
      return rows + (rows * racks) + (rows * racks * positions);

    case 4:
      // Filas + (Filas × Racks) + (Filas × Racks × Niveles) + (Filas × Racks × Niveles × Posiciones)
      return rows + (rows * racks) + (rows * racks * levels) + (rows * racks * levels * positions);

    default:
      return 0;
  }
}

/**
 * Valida que las dimensiones sean válidas para la profundidad especificada
 * 
 * @param config - Configuración a validar
 * @returns Array de mensajes de error (vacío si es válido)
 */
export function validateDimensions(config: BatchCreationConfig): string[] {
  const errors: string[] = [];
  const { depth, dimensions } = config;
  const { rows, racks, levels, positions } = dimensions;

  // Validar posiciones (siempre requerido)
  if (!positions || positions < 1) {
    errors.push('Las posiciones son requeridas y deben ser al menos 1');
  }
  if (positions && positions > 999) {
    errors.push('Las posiciones no pueden exceder 999');
  }

  // Validar según profundidad
  switch (depth) {
    case 2:
      if (!rows || rows < 1) {
        errors.push('Las filas son requeridas para profundidad 2 y deben ser al menos 1');
      }
      if (rows && rows > 999) {
        errors.push('Las filas no pueden exceder 999');
      }
      break;

    case 3:
      if (!rows || rows < 1) {
        errors.push('Las filas son requeridas para profundidad 3 y deben ser al menos 1');
      }
      if (rows && rows > 999) {
        errors.push('Las filas no pueden exceder 999');
      }
      if (!racks || racks < 1) {
        errors.push('Los racks son requeridos para profundidad 3 y deben ser al menos 1');
      }
      if (racks && racks > 999) {
        errors.push('Los racks no pueden exceder 999');
      }
      break;

    case 4:
      if (!rows || rows < 1) {
        errors.push('Las filas son requeridas para profundidad 4 y deben ser al menos 1');
      }
      if (rows && rows > 999) {
        errors.push('Las filas no pueden exceder 999');
      }
      if (!racks || racks < 1) {
        errors.push('Los racks son requeridos para profundidad 4 y deben ser al menos 1');
      }
      if (racks && racks > 999) {
        errors.push('Los racks no pueden exceder 999');
      }
      if (!levels || levels < 1) {
        errors.push('Los niveles son requeridos para profundidad 4 y deben ser al menos 1');
      }
      if (levels && levels > 999) {
        errors.push('Los niveles no pueden exceder 999');
      }
      break;
  }

  return errors;
}

/**
 * Valida que los prefijos personalizados sean válidos
 * 
 * @param prefixes - Prefijos a validar
 * @returns Array de mensajes de error (vacío si es válido)
 */
export function validatePrefixes(prefixes?: NomenclaturePrefixes): string[] {
  const errors: string[] = [];

  if (!prefixes) return errors;

  const { row, rack, level, position } = prefixes;

  if (row !== undefined && !isValidPrefix(row)) {
    errors.push('El prefijo de fila debe tener entre 0-10 caracteres alfanuméricos o guiones');
  }

  if (rack !== undefined && !isValidPrefix(rack)) {
    errors.push('El prefijo de rack debe tener entre 0-10 caracteres alfanuméricos o guiones');
  }

  if (level !== undefined && !isValidPrefix(level)) {
    errors.push('El prefijo de nivel debe tener entre 0-10 caracteres alfanuméricos o guiones');
  }

  if (position !== undefined && !isValidPrefix(position)) {
    errors.push('El prefijo de posición debe tener entre 0-10 caracteres alfanuméricos o guiones');
  }

  return errors;
}
